import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormControlLabel,
  Switch,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Checkbox,
  Grid,
  Paper,
  Divider,
  Fade,
  alpha,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Delete,
  DragIndicator,
  Add,
  Save,
  Widgets,
  Store,
  Category,
  Inventory,
  Code,
  Settings,
  CheckCircle,
  CloudDownload,
  Check,
  ContentCopy,
  Launch,
  Refresh,
  Edit,
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import SearchInput from '../components/common/SearchInput';
import { PageLoader } from '../components/common/LoadingState';
import api from '../services/api';

const steps = [
  { label: 'Retail y Categoría', icon: <Store /> },
  { label: 'Productos', icon: <Inventory /> },
  { label: 'Campos', icon: <Code /> },
  { label: 'Configuración', icon: <Settings /> },
  { label: 'Código', icon: <Code /> },
];

const WidgetWizard = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [retails, setRetails] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    retailId: '',
    categoryId: '',
    productSkus: [],
    selectedFields: [],
    urlPatterns: [],
    brands: [],
    orderMode: 'manual',
    triggerOnSku: false,
    isActive: true,
  });

  const [newUrlPattern, setNewUrlPattern] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [searchProduct, setSearchProduct] = useState('');
  
  // Track if order has been modified from default
  const [orderModified, setOrderModified] = useState(false);
  const [defaultProductSkus, setDefaultProductSkus] = useState([]);
  
  // Saved widget state for code generation
  const [savedWidgetId, setSavedWidgetId] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  
  // Import specs state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState('loading'); // 'loading', 'fields', 'applying'
  const [importJobId, setImportJobId] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importFields, setImportFields] = useState([]);
  const [importFieldExamples, setImportFieldExamples] = useState({});
  const [selectedImportFields, setSelectedImportFields] = useState([]);
  const [importSuccess, setImportSuccess] = useState('');

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [retailsRes, categoriesRes, productsRes] = await Promise.all([
          api.get('/retails'),
          api.get('/categories'),
          api.get('/products?limit=10000'),
        ]);
        setRetails(retailsRes.data);
        setCategories(categoriesRes.data);
        setProducts(productsRes.data.data || []);

        // If editing, load widget data
        if (id) {
          const widgetRes = await api.get(`/widgets/${id}`);
          const widget = widgetRes.data;
          setFormData({
            name: widget.name,
            retailId: widget.retailId,
            categoryId: widget.categoryId || '',
            productSkus: widget.productSkus || [],
            selectedFields: widget.selectedFields || [],
            urlPatterns: widget.urlPatterns || [],
            brands: widget.brands || [],
            orderMode: widget.orderMode || 'manual',
            triggerOnSku: widget.triggerOnSku || false,
            isActive: widget.isActive,
          });
        }
      } catch (err) {
        setError('Error al cargar datos');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Load default products when retail/category changes (only if not editing or order not modified)
  useEffect(() => {
    if (!formData.retailId || products.length === 0) return;
    
    // If editing and already has products, mark as already modified to preserve the saved order
    if (isEditing && formData.productSkus.length > 0 && !orderModified) {
      setOrderModified(true);
      setDefaultProductSkus([...formData.productSkus]);
      return;
    }
    
    // Don't auto-load if order was manually modified
    if (orderModified) return;
    
    // Filter products for this retail/category
    const retailProducts = products.filter(p => {
      if (p.retailId !== formData.retailId) return false;
      if (formData.categoryId && p.categoryId !== formData.categoryId) return false;
      return true;
    });
    
    // Create default product SKUs (limited to first 20 products)
    const defaultSkus = retailProducts.slice(0, 20).map((p, index) => ({
      sku: p.sku,
      order: index
    }));
    
    setDefaultProductSkus(defaultSkus);
    setFormData(prev => ({
      ...prev,
      productSkus: defaultSkus
    }));
  }, [formData.retailId, formData.categoryId, products, isEditing]);

  // Update available fields when products are selected
  useEffect(() => {
    if (formData.productSkus.length > 0) {
      const selectedProducts = products.filter(p =>
        formData.productSkus.some(ps => ps.sku === p.sku)
      );

      const allFields = new Set();
      selectedProducts.forEach(product => {
        if (product.specs) {
          Object.keys(product.specs).forEach(key => allFields.add(key));
        }
      });

      const fieldsList = Array.from(allFields).map(key => {
        const examples = selectedProducts
          .map(p => p.specs?.[key])
          .filter(Boolean)
          .slice(0, 2);

        return {
          key,
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
          examples,
        };
      });

      setAvailableFields(fieldsList);
    }
  }, [formData.productSkus, products]);

  const handleNext = () => {
    setError('');

    // Validation
    if (activeStep === 0) {
      if (!formData.retailId) {
        setError('Debes seleccionar un retail');
        return;
      }
    } else if (activeStep === 1) {
      if (formData.productSkus.length === 0) {
        setError('Debes seleccionar al menos un producto');
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setError('El nombre del widget es requerido');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        selectedFields: formData.selectedFields.map(f =>
          typeof f === 'string' ? { key: f, label: f } : f
        ),
      };

      let response;
      if (isEditing) {
        response = await api.put(`/widgets/${id}`, payload);
        setSavedWidgetId(id);
      } else {
        response = await api.post('/widgets', payload);
        setSavedWidgetId(response.data.id);
      }

      // Move to code step instead of navigating away
      setActiveStep(4);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar widget');
    } finally {
      setLoading(false);
    }
  };
  
  // Copy code to clipboard
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };
  
  // Get the API base URL
  const getApiBaseUrl = () => {
    return window.location.origin.replace(':3000', ':3001');
  };

  const filteredCategories = formData.retailId
    ? categories.filter(c => c.retailId === formData.retailId)
    : [];

  // Get selected product SKUs for quick lookup
  const selectedSkus = new Set(formData.productSkus.map(p => p.sku));
  
  // Filter products: when searching, search against ALL products
  // When not searching, filter by retail/category and limit to 10
  // Always include already selected products at the top
  const { filteredProducts, totalFilteredCount } = (() => {
    // Get already selected products (always show these)
    const selectedProducts = products.filter(p => selectedSkus.has(p.sku));
    
    let searchResults = [];
    let totalCount = 0;
    
    if (searchProduct && searchProduct.trim().length > 0) {
      // Search against ALL products (ignoring retail/category filters)
      const searchTerm = searchProduct.toLowerCase().trim();
      searchResults = products.filter(p => {
        if (selectedSkus.has(p.sku)) return false; // Exclude already selected
        const title = p.feedData?.title || p.catalogData?.name || p.title || '';
        const brand = p.feedData?.brand || p.brand || '';
        return (
          p.sku?.toLowerCase().includes(searchTerm) ||
          title.toLowerCase().includes(searchTerm) ||
          brand.toLowerCase().includes(searchTerm)
        );
      });
      totalCount = searchResults.length + selectedProducts.length;
    } else {
      // No search: filter by retail/category
      searchResults = products.filter(p => {
        if (selectedSkus.has(p.sku)) return false; // Exclude already selected
        if (formData.retailId && p.retailId !== formData.retailId) return false;
        if (formData.categoryId && p.categoryId !== formData.categoryId) return false;
        return true;
      });
      totalCount = searchResults.length + selectedProducts.length;
    }
    
    // Combine: selected products first, then search results (limited to fill up to 10)
    const remainingSlots = Math.max(0, 10 - selectedProducts.length);
    const result = [...selectedProducts, ...searchResults.slice(0, remainingSlots)];
    
    return { filteredProducts: result, totalFilteredCount: totalCount };
  })();

  const addProduct = (product) => {
    if (!formData.productSkus.some(p => p.sku === product.sku)) {
      setFormData({
        ...formData,
        productSkus: [
          ...formData.productSkus,
          { sku: product.sku, order: formData.productSkus.length }
        ],
      });
      setOrderModified(true);
    }
  };

  const removeProduct = (sku) => {
    setFormData({
      ...formData,
      productSkus: formData.productSkus
        .filter(p => p.sku !== sku)
        .map((p, i) => ({ ...p, order: i })),
    });
    setOrderModified(true);
  };
  
  // Reset to default order
  const resetToDefaultOrder = () => {
    if (defaultProductSkus.length > 0) {
      setFormData({
        ...formData,
        productSkus: [...defaultProductSkus]
      });
      setOrderModified(false);
    }
  };

  const toggleField = (fieldKey) => {
    const isSelected = formData.selectedFields.some(f =>
      (typeof f === 'string' ? f : f.key) === fieldKey
    );

    if (isSelected) {
      setFormData({
        ...formData,
        selectedFields: formData.selectedFields.filter(f =>
          (typeof f === 'string' ? f : f.key) !== fieldKey
        ),
      });
    } else {
      if (formData.selectedFields.length >= 60) {
        setError('Máximo 60 campos permitidos');
        return;
      }
      const field = availableFields.find(f => f.key === fieldKey);
      setFormData({
        ...formData,
        selectedFields: [...formData.selectedFields, field],
      });
    }
  };

  const addUrlPattern = () => {
    if (newUrlPattern && !formData.urlPatterns.includes(newUrlPattern)) {
      setFormData({
        ...formData,
        urlPatterns: [...formData.urlPatterns, newUrlPattern],
      });
      setNewUrlPattern('');
    }
  };

  const addBrand = () => {
    if (newBrand && !formData.brands.includes(newBrand.toLowerCase())) {
      setFormData({
        ...formData,
        brands: [...formData.brands, newBrand.toLowerCase()],
      });
      setNewBrand('');
    }
  };

  // Start import specs for selected products
  const startImportSpecs = async () => {
    const selectedSkus = formData.productSkus.map(p => p.sku);
    
    if (selectedSkus.length === 0) {
      setError('No hay productos seleccionados para importar specs');
      return;
    }

    setImportDialogOpen(true);
    setImportStep('loading');
    setImportProgress(0);
    setImportFields([]);
    setImportFieldExamples({});
    setSelectedImportFields([]);

    try {
      // Use the specs import endpoint
      const response = await api.post('/specs/import/start', {
        skus: selectedSkus,
        lang: 'PT'
      });

      const jobId = response.data.jobId;
      setImportJobId(jobId);

      // Poll for progress
      pollImportProgress(jobId);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al iniciar importación de specs');
      setImportDialogOpen(false);
    }
  };

  // Poll import progress
  const pollImportProgress = async (jobId) => {
    try {
      const response = await api.get(`/specs/import/status/${jobId}`);
      const job = response.data;

      setImportProgress(job.progress);

      if (job.status === 'completed') {
        setImportFields(job.allFields || []);
        setImportFieldExamples(job.fieldExamples || {});
        setImportStep('fields');
        
        // Pre-select useful fields
        const usefulFields = (job.allFields || []).filter(f => 
          !['sku', 'id', 'createdAt', 'updatedAt', '_id', 'created', 'modified'].includes(f)
        );
        setSelectedImportFields(usefulFields.slice(0, 15));
      } else if (job.status === 'processing') {
        setTimeout(() => pollImportProgress(jobId), 1000);
      } else {
        setError('Error en la importación');
        setImportDialogOpen(false);
      }
    } catch (err) {
      setError('Error al verificar progreso de importación');
      setImportDialogOpen(false);
    }
  };

  // Finalize import
  const finalizeImport = async () => {
    if (selectedImportFields.length === 0) {
      setError('Selecciona al menos un campo para importar');
      return;
    }

    setImportStep('applying');

    try {
      await api.post(`/specs/import/finalize/${importJobId}`, {
        selectedFields: selectedImportFields,
        lang: 'PT'
      });

      setImportDialogOpen(false);
      setImportSuccess('Specs importados exitosamente');
      
      // Refresh products to get new specs
      const productsRes = await api.get('/products?limit=10000');
      setProducts(productsRes.data.data || []);
      
      // Clear success message after a few seconds
      setTimeout(() => setImportSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al aplicar specs');
      setImportStep('fields');
    }
  };

  // Cancel import
  const cancelImport = async () => {
    if (importJobId) {
      try {
        await api.delete(`/specs/import/${importJobId}`);
      } catch (err) {
        // Ignore
      }
    }
    setImportDialogOpen(false);
  };

  // Toggle field for import
  const toggleImportField = (field) => {
    if (selectedImportFields.includes(field)) {
      setSelectedImportFields(selectedImportFields.filter(f => f !== field));
    } else {
      if (selectedImportFields.length >= 60) {
        setError('Máximo 60 campos permitidos');
        return;
      }
      setSelectedImportFields([...selectedImportFields, field]);
    }
  };

  if (initialLoading) {
    return <PageLoader message="Cargando configuración..." />;
  }

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Selecciona el retail y categoría para el widget
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                El widget mostrará productos del retail seleccionado
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Retail *</InputLabel>
                <Select
                  value={formData.retailId}
                  label="Retail *"
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      retailId: e.target.value,
                      categoryId: '',
                      productSkus: [],
                    });
                    setOrderModified(false);
                    setDefaultProductSkus([]);
                  }}
                >
                  {retails.map((retail) => (
                    <MenuItem key={retail.id} value={retail.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Store sx={{ fontSize: 18, color: 'text.secondary' }} />
                        {retail.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Categoría (Opcional)</InputLabel>
                <Select
                  value={formData.categoryId}
                  label="Categoría (Opcional)"
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      categoryId: e.target.value,
                      productSkus: []
                    });
                    setOrderModified(false);
                    setDefaultProductSkus([]);
                  }}
                  disabled={!formData.retailId}
                >
                  <MenuItem value="">
                    <em>Todas las categorías</em>
                  </MenuItem>
                  {filteredCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Category sx={{ fontSize: 18, color: 'text.secondary' }} />
                        {category.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Productos Disponibles
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={totalFilteredCount > 10 ? `10 de ${totalFilteredCount}` : totalFilteredCount} 
                    size="small" 
                    color={searchProduct ? 'primary' : 'default'}
                  />
                </Box>
              </Box>
              <SearchInput
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
                placeholder="Buscar en todos los productos..."
                fullWidth
                sx={{ mb: 2 }}
              />
              {searchProduct && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Buscando en todos los productos del catálogo
                </Typography>
              )}
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  borderRadius: 2,
                }}
              >
                {filteredProducts.length === 0 ? (
                  <EmptyState
                    icon="search"
                    title="Sin resultados"
                    description={searchProduct ? "No se encontraron productos. Intenta con otro término." : "No hay productos disponibles"}
                    size="small"
                  />
                ) : (
                  <>
                    <List dense disablePadding>
                      {filteredProducts.map((product) => {
                        const isSelected = formData.productSkus.some(p => p.sku === product.sku);
                        return (
                          <ListItem
                            key={product.id}
                            button
                            onClick={() => !isSelected && addProduct(product)}
                            disabled={isSelected}
                            sx={{
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              opacity: isSelected ? 0.5 : 1,
                              '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                              },
                            }}
                          >
                            <ListItemText
                              primary={product.feedData?.title || product.catalogData?.name || product.title || product.sku}
                              secondary={`SKU: ${product.sku} ${product.feedData?.brand || product.brand ? `| ${product.feedData?.brand || product.brand}` : ''}`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                            {!isSelected && (
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => addProduct(product)}
                                  color="primary"
                                >
                                  <Add fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            )}
                            {isSelected && (
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                            )}
                          </ListItem>
                        );
                      })}
                    </List>
                    {totalFilteredCount > 10 && (
                      <Box sx={{ p: 2, textAlign: 'center', backgroundColor: (theme) => alpha(theme.palette.info.main, 0.05) }}>
                        <Typography variant="caption" color="text.secondary">
                          Mostrando 10 de {totalFilteredCount} productos. 
                          {!searchProduct && ' Usa el buscador para encontrar más.'}
                          {searchProduct && ' Refina tu búsqueda para ver más resultados.'}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Productos Seleccionados
                  </Typography>
                  {orderModified && (
                    <Chip
                      label="Modificado"
                      size="small"
                      color="warning"
                      icon={<Edit sx={{ fontSize: 14 }} />}
                      sx={{ height: 22 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {orderModified && defaultProductSkus.length > 0 && (
                    <Tooltip title="Restaurar orden por defecto">
                      <IconButton
                        size="small"
                        onClick={resetToDefaultOrder}
                        sx={{ color: 'text.secondary' }}
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Chip
                    label={formData.productSkus.length}
                    size="small"
                    color={formData.productSkus.length > 0 ? 'primary' : 'default'}
                  />
                </Box>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 452,
                  overflow: 'auto',
                  borderRadius: 2,
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.02),
                }}
              >
                {formData.productSkus.length === 0 ? (
                  <EmptyState
                    icon="empty"
                    title="Sin productos"
                    description="Selecciona productos de la lista"
                    size="small"
                  />
                ) : (
                  <List dense disablePadding>
                    {formData.productSkus.map((ps, index) => {
                      const product = products.find(p => p.sku === ps.sku);
                      return (
                        <ListItem
                          key={ps.sku}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              mr: 1.5,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <ListItemText
                            primary={product?.feedData?.title || product?.catalogData?.name || product?.title || ps.sku}
                            secondary={`SKU: ${ps.sku}`}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => removeProduct(ps.sku)}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Seleccionar Campos a Mostrar
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Seleccionados: <strong>{formData.selectedFields.length}</strong> / 60 campos
                </Typography>
              </Box>
              {availableFields.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudDownload />}
                  onClick={startImportSpecs}
                >
                  Importar más specs
                </Button>
              )}
            </Box>

            {importSuccess && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                {importSuccess}
              </Alert>
            )}

            {availableFields.length === 0 ? (
              <Box>
                <Alert 
                  severity="info" 
                  sx={{ borderRadius: 2, mb: 3 }}
                  action={
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={startImportSpecs}
                      startIcon={<CloudDownload />}
                    >
                      Importar Ahora
                    </Button>
                  }
                >
                  Los productos seleccionados no tienen especificaciones.
                </Alert>
                
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    borderRadius: 2,
                    borderStyle: 'dashed',
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.02)
                  }}
                >
                  <CloudDownload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Importar Especificaciones
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Obtiene las especificaciones de los {formData.productSkus.length} productos seleccionados 
                    desde la API externa (Alquimio).
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<CloudDownload />}
                    onClick={startImportSpecs}
                  >
                    Importar Specs de {formData.productSkus.length} Productos
                  </Button>
                </Paper>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {availableFields.map((field) => {
                  const isSelected = formData.selectedFields.some(f =>
                    (typeof f === 'string' ? f : f.key) === field.key
                  );
                  return (
                    <Grid item xs={12} sm={6} md={4} key={field.key}>
                      <Paper
                        variant="outlined"
                        onClick={() => toggleField(field.key)}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          borderRadius: 2,
                          borderWidth: isSelected ? 2 : 1,
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          backgroundColor: isSelected
                            ? (theme) => alpha(theme.palette.primary.main, 0.04)
                            : 'background.paper',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Checkbox
                            checked={isSelected}
                            size="small"
                            sx={{ mt: -0.5, ml: -1, mr: 0.5 }}
                          />
                          <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {field.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              key: {field.key}
                            </Typography>
                            {field.examples.length > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Ej: {field.examples.slice(0, 2).map(e =>
                                  typeof e === 'object' ? JSON.stringify(e) : String(e)
                                ).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Widget *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                helperText="Nombre descriptivo para identificar el widget"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Modo de Ordenamiento</InputLabel>
                <Select
                  value={formData.orderMode}
                  label="Modo de Ordenamiento"
                  onChange={(e) => setFormData({ ...formData, orderMode: e.target.value })}
                >
                  <MenuItem value="manual">Manual (orden definido)</MenuItem>
                  <MenuItem value="ai">IA (automático)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Patrones de URL
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="Patrón de URL (regex)"
                  value={newUrlPattern}
                  onChange={(e) => setNewUrlPattern(e.target.value)}
                  size="small"
                  placeholder=".*producto.*"
                  onKeyPress={(e) => e.key === 'Enter' && addUrlPattern()}
                />
                <Button variant="outlined" onClick={addUrlPattern}>
                  Agregar
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {formData.urlPatterns.map((pattern, i) => (
                  <Chip
                    key={i}
                    label={pattern}
                    onDelete={() => setFormData({
                      ...formData,
                      urlPatterns: formData.urlPatterns.filter((_, idx) => idx !== i),
                    })}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Filtro por Marcas
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="Marca"
                  value={newBrand}
                  onChange={(e) => setNewBrand(e.target.value)}
                  size="small"
                  onKeyPress={(e) => e.key === 'Enter' && addBrand()}
                />
                <Button variant="outlined" onClick={addBrand}>
                  Agregar
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {formData.brands.map((brand, i) => (
                  <Chip
                    key={i}
                    label={brand}
                    onDelete={() => setFormData({
                      ...formData,
                      brands: formData.brands.filter((_, idx) => idx !== i),
                    })}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.triggerOnSku}
                    onChange={(e) => setFormData({ ...formData, triggerOnSku: e.target.checked })}
                  />
                }
                label="Activar solo cuando SKU coincide"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Widget Activo"
              />
            </Grid>
          </Grid>
        );

      case 4:
        // Code step - show embed code after saving
        const widgetId = savedWidgetId || id;
        const apiBaseUrl = getApiBaseUrl();
        
        const scriptCode = `<!-- Upselling Widget - ${formData.name} -->
<script src="${apiBaseUrl}/api/widgets/script/${widgetId}"></script>
<div id="upselling-widget-container"></div>`;

        const jsCode = `// Configuración opcional del widget
window.UpsellingWidgetConfig = {
  containerId: 'upselling-widget-container',
  title: 'Productos Recomendados',
  maxProducts: 4,
  showSpecs: true,
  showRating: true,
  showPrice: true
};`;

        const fetchCode = `// Obtener datos del widget via fetch
fetch('${apiBaseUrl}/api/widgets/data/${widgetId}')
  .then(response => response.json())
  .then(data => {
    console.log('Widget:', data.widget);
    console.log('Productos:', data.products);
    // Renderizar tu propia UI aquí
  });`;

        const jsonpCode = `// Obtener datos via JSONP (para sitios sin CORS)
function handleWidgetData(data) {
  console.log('Widget:', data.widget);
  console.log('Productos:', data.products);
  // Renderizar tu propia UI aquí
}

// Agregar script
var script = document.createElement('script');
script.src = '${apiBaseUrl}/api/widgets/data/${widgetId}?callback=handleWidgetData';
document.body.appendChild(script);`;

        return (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: (theme) => alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main', mb: 1 }}>
                Widget {isEditing ? 'Actualizado' : 'Creado'} Exitosamente
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Usa el código a continuación para insertar el widget en tu sitio
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Option 1: Script Tag */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: (theme) => alpha(theme.palette.grey[900], 0.02),
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Opción 1: Script Tag (Recomendado)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inserta este código en tu página donde quieres mostrar el widget
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={codeCopied ? <Check /> : <ContentCopy />}
                      onClick={() => handleCopyCode(scriptCode)}
                      color={codeCopied ? 'success' : 'primary'}
                    >
                      {codeCopied ? 'Copiado!' : 'Copiar'}
                    </Button>
                  </Box>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: '#1e1e1e',
                      borderRadius: 1,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        m: 0,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        color: '#d4d4d4',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {scriptCode}
                    </Typography>
                  </Paper>
                </Paper>
              </Grid>

              {/* Option 2: Custom Config */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Configuración Personalizada
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Agrega antes del script para personalizar
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleCopyCode(jsCode)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                  <Paper
                    sx={{
                      p: 1.5,
                      backgroundColor: '#1e1e1e',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 200,
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        m: 0,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#9cdcfe',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {jsCode}
                    </Typography>
                  </Paper>
                </Paper>
              </Grid>

              {/* Option 3: Fetch API */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Opción 2: Fetch API
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Para implementación personalizada
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleCopyCode(fetchCode)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                  <Paper
                    sx={{
                      p: 1.5,
                      backgroundColor: '#1e1e1e',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 200,
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        m: 0,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#9cdcfe',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {fetchCode}
                    </Typography>
                  </Paper>
                </Paper>
              </Grid>

              {/* Option 4: JSONP */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Opción 3: JSONP (Sin CORS)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Para sitios que no soportan CORS
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleCopyCode(jsonpCode)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Box>
                  <Paper
                    sx={{
                      p: 1.5,
                      backgroundColor: '#1e1e1e',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 150,
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        m: 0,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#9cdcfe',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {jsonpCode}
                    </Typography>
                  </Paper>
                </Paper>
              </Grid>

              {/* Endpoints Info */}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Endpoints disponibles:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li>
                      <Typography variant="body2">
                        <strong>Script:</strong>{' '}
                        <code>{apiBaseUrl}/api/widgets/script/{widgetId}</code>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Data JSON:</strong>{' '}
                        <code>{apiBaseUrl}/api/widgets/data/{widgetId}</code>
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Embed:</strong>{' '}
                        <code>{apiBaseUrl}/api/widgets/embed/{widgetId}</code>
                      </Typography>
                    </li>
                  </Box>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <PageHeader
        title={isEditing ? 'Editar Widget' : 'Nuevo Widget de Upselling'}
        subtitle="Configura un widget paso a paso"
        showBackButton
        backPath="/widgets"
        breadcrumbs={[
          { label: 'Widgets', path: '/widgets' },
          { label: isEditing ? 'Editar' : 'Nuevo' },
        ]}
      />

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: activeStep >= index
                          ? 'primary.main'
                          : (theme) => alpha(theme.palette.grey[500], 0.2),
                        color: activeStep >= index ? 'white' : 'text.secondary',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {activeStep > index ? (
                        <CheckCircle sx={{ fontSize: 18 }} />
                      ) : (
                        React.cloneElement(step.icon, { sx: { fontSize: 16 } })
                      )}
                    </Box>
                  )}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: activeStep === index ? 600 : 400,
                      color: activeStep >= index ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ minHeight: 350 }}>
            {renderStepContent(activeStep)}
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {activeStep === 4 ? (
              // Code step - show "Go to Widgets" button
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    // Reset form for new widget
                    setActiveStep(0);
                    setSavedWidgetId(null);
                    setFormData({
                      name: '',
                      retailId: '',
                      categoryId: '',
                      productSkus: [],
                      selectedFields: [],
                      urlPatterns: [],
                      brands: [],
                      orderMode: 'manual',
                      triggerOnSku: false,
                      isActive: true,
                    });
                  }}
                  startIcon={<Add />}
                >
                  Crear Otro Widget
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/widgets')}
                  endIcon={<Launch />}
                >
                  Ir a Widgets
                </Button>
              </Box>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/widgets')}
                  color="inherit"
                >
                  Cancelar
                </Button>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    startIcon={<ArrowBack />}
                    variant="outlined"
                  >
                    Atrás
                  </Button>
                  {activeStep === 3 ? (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={loading}
                      startIcon={<Save />}
                    >
                      {loading ? 'Guardando...' : 'Guardar Widget'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowForward />}
                    >
                      Siguiente
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Import Specs Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={cancelImport}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudDownload color="primary" />
            Importar Especificaciones
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Loading Step */}
          {importStep === 'loading' && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Obteniendo especificaciones...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Consultando API externa para {formData.productSkus.length} productos
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={importProgress} 
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {importProgress}% completado
              </Typography>
            </Box>
          )}

          {/* Select Fields Step */}
          {importStep === 'fields' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Selecciona los campos que deseas importar (maximo 60).
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  {selectedImportFields.length} de {importFields.length} campos seleccionados
                </Typography>
                <Chip 
                  label={`Maximo 60`} 
                  size="small" 
                  color={selectedImportFields.length >= 60 ? 'error' : 'default'}
                />
              </Box>

              <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                <List dense>
                  {importFields.map((field) => (
                    <ListItem
                      key={field}
                      button
                      onClick={() => toggleImportField(field)}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: selectedImportFields.includes(field) 
                          ? (theme) => alpha(theme.palette.primary.main, 0.05)
                          : 'transparent'
                      }}
                    >
                      <Checkbox
                        checked={selectedImportFields.includes(field)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          key: {field}
                        </Typography>
                        {importFieldExamples[field] && importFieldExamples[field].length > 0 && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            Ej: {importFieldExamples[field].slice(0, 2).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {importFields.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No se encontraron especificaciones para los SKUs seleccionados. 
                  Verifica que los SKUs existan en la API externa.
                </Alert>
              )}
            </Box>
          )}

          {/* Applying Step */}
          {importStep === 'applying' && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Guardando especificaciones...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Aplicando {selectedImportFields.length} campos a los productos
              </Typography>
              <LinearProgress sx={{ mt: 3 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {importStep === 'loading' && (
            <Button onClick={cancelImport} color="error">
              Cancelar
            </Button>
          )}
          {importStep === 'fields' && (
            <>
              <Button onClick={cancelImport}>Cancelar</Button>
              <Button 
                variant="contained" 
                onClick={finalizeImport}
                disabled={selectedImportFields.length === 0}
                startIcon={<Check />}
              >
                Importar {selectedImportFields.length} campos
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WidgetWizard;
